if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.updateAuziLowStock(this.getLocalSelectedVariant());
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const isHearloom = this.dataset.url.split('?')[0].endsWith('/hearloom');
        if (isHearloom) this.productForm?.handleErrorMessage();
        else this.resetProductFormState();

        const optionControl = event.target.closest('select, input');
        const optionName = optionControl?.name || '';
        const visualType = optionControl?.closest('[data-auzi-visual-type]')?.dataset.auziVisualType;
        const scrollPhotoForOption =
          Boolean(visualType) ||
          (/gem|stone|metal|colou?r|finish|pierre|gemme|métal|metall|couleur|finition/i.test(optionName) &&
          !/ear|pair|quantity|single|\bset\b|side|oreille|paire|seul/i.test(optionName));
        const scrollPhotoOnMobile =
          window.matchMedia('(max-width: 989px)').matches && scrollPhotoForOption;
        const scrollPhotoOnDesktop =
          window.matchMedia('(min-width: 990px)').matches && scrollPhotoForOption;

        const productUrl = target.dataset.productUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;

        if (shouldSwapProduct) {
          const shouldFetchFullPage = this.dataset.updateUrl === 'true';

          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
            targetId: target.id,
            callback: this.handleSwapProduct(productUrl, shouldFetchFullPage),
          });

          return;
        }

        // Native mobile pickers can emit input before their final change event.
        // Update from the embedded model immediately; network rendering enriches it.
        const localVariant = this.getLocalSelectedVariant();
        if (localVariant) {
          const selectedNode = this.variantSelectors.querySelector('[data-selected-variant]');
          if (selectedNode) selectedNode.textContent = JSON.stringify(localVariant);
          this.updateVariantInputs(localVariant.id);
          this.updateURL(productUrl, localVariant.id);
          this.updateLocalPrice(localVariant);
          this.updateAuziLowStock(localVariant);
          this.updateHearloomState(localVariant);
          this.updateLocalMedia(localVariant, true);
          this.productForm?.toggleSubmitButton(!localVariant.available, window.variantStrings.soldOut);
          if (scrollPhotoOnMobile) this.scrollToProductMediaOnMobile();
        }

        this.renderProductInfo({
          requestUrl: isHearloom && localVariant
            ? `${productUrl}?section_id=${this.sectionId}&variant=${localVariant.id}`
            : this.buildRequestUrlWithParams(productUrl, selectedOptionValues, false),
          targetId: target.id,
          callback: this.handleUpdateProductInfo(productUrl, true, scrollPhotoOnDesktop),
        });
      }

      scrollToProductMediaOnMobile() {
        if (!window.matchMedia('(max-width: 989px)').matches) return;

        window.requestAnimationFrame(() => {
          window.scrollTo({
            top: 0,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        });
      }

      getLocalVariants() {
        if (this.localVariants) return this.localVariants;
        const node = this.querySelector('[data-auzi-variants]');
        if (!node) return [];
        try {
          this.localVariants = JSON.parse(node.textContent);
        } catch (error) {
          console.error(error);
          this.localVariants = [];
        }
        return this.localVariants;
      }

      getLocalSelectedVariant() {
        const selectors = this.variantSelectors;
        if (!selectors) return null;

        const selectedValues = getProductOptionControls(selectors).map((select) => select.value);

        return (
          this.getLocalVariants().find(
            (variant) =>
              variant.options.length === selectedValues.length &&
              variant.options.every((value, index) => value === selectedValues[index])
          ) || null
        );
      }

      updateAuziLowStock(variant) {
        const badge = this.querySelector('[data-auzi-low-stock]');
        if (!badge) return;

        const text = badge.querySelector('[data-auzi-low-stock-text]');
        const storedVariant = this.getLocalVariants().find((candidate) => String(candidate.id) === String(variant?.id));
        const rawQuantity = variant?.inventoryQuantity ?? variant?.inventory_quantity ?? storedVariant?.inventoryQuantity;
        const quantity = Number(rawQuantity);

        const shouldShow =
          variant?.available === true &&
          Number.isFinite(quantity) &&
          quantity > 0 &&
          quantity <= 5;

        if (!shouldShow) {
          badge.hidden = true;
          if (text) text.textContent = '';
          return;
        }

        if (text) {
          text.textContent = quantity === 1 ? '1 remaining' : `${quantity} remaining`;
        }
        badge.hidden = false;
      }

      updateHearloomState(variant) {
        if (!this.dataset.url.split('?')[0].endsWith('/hearloom') || !variant) return;
        const variants = this.getLocalVariants();
        getProductOptionControls(this.variantSelectors, 'select').forEach((select, index) => {
          Array.from(select.options).forEach((option) => {
            const available = variants.some((candidate) =>
              candidate.available && candidate.options[index] === option.value &&
              candidate.options.slice(0, index).every((value, position) => value === variant.options[position])
            );
            option.textContent = available ? option.value : (option.dataset.auziUnavailableLabel || option.textContent.trim());
            option.toggleAttribute('selected', option.value === select.value);
          });
          const display = select.closest('[data-auzi-visual-wrap]')?.querySelector('[data-auzi-visual-value]');
          if (display) display.textContent = select.selectedOptions[0]?.textContent.trim() || select.value;
        });
        this.productForm?.toggleSubmitButton(!variant.available, window.variantStrings.soldOut);
        const submit = this.querySelector('.product-form__submit');
        const notify = this.querySelector('.auzi-notify-trigger');
        const checkout = this.querySelector('.auzi-dynamic-checkout');
        submit?.style.setProperty('display', variant.available ? 'flex' : 'none', 'important');
        notify?.style.setProperty('display', variant.available ? 'none' : 'block', 'important');
        checkout?.style.setProperty('display', variant.available ? 'block' : 'none', 'important');
        this.querySelector('.auzi-product-payment-terms')?.classList.toggle('is-hidden', !variant.available);
      }

      updateLocalPrice(variant) {
        const container = this.querySelector(`#price-${this.dataset.section}`);
        const price = container?.querySelector('.price');
        if (!container || !price) return;

        container.classList.remove('hidden');

        const onSale =
          Number(variant.compareAtPrice || 0) > Number(variant.price || 0);

        price.classList.toggle('price--sold-out', !variant.available);
        price.classList.toggle('price--on-sale', onSale);

        const regular = price.querySelector('.price__regular .price-item--regular');
        const sale = price.querySelector('.price__sale .price-item--sale');
        const compare = price.querySelector('.price__sale s.price-item--regular');

        const priceRoot = price.querySelector('[data-auzi-price-root]');
        if (priceRoot) priceRoot.dataset.auziBasePriceCents = String(variant.price || 0);
        window.dispatchEvent(new CustomEvent('auzi:variant-price-changed', { detail: { variantId: variant.id, available: variant.available, priceCents: variant.price || 0 } }));
        if (regular) regular.textContent = variant.priceText || '';
        if (sale) sale.textContent = variant.priceText || '';
        if (compare) compare.textContent = onSale ? variant.compareText || '' : '';
      }

      updateLocalMedia(variant, preventPageScroll = false) {
        if (!variant.featuredMediaId) return;

        const gallery = this.querySelector('media-gallery');
        if (!gallery) return;

        const mediaId = `${this.dataset.section}-${variant.featuredMediaId}`;
        const existingMedia = gallery.querySelector(`[data-media-id="${mediaId}"]`);

        if (existingMedia) {
          gallery.setActiveMedia?.(mediaId, true, preventPageScroll);
          return;
        }

        const modalImage = this.productModal?.querySelector(
          `img[data-media-id="${variant.featuredMediaId}"]`
        );
        const activeMedia =
          gallery.querySelector('[data-media-id].is-active') ||
          gallery.querySelector('[data-media-id]');
        const activeImage =
          activeMedia?.querySelector('.product__media img') ||
          activeMedia?.querySelector('img');

        if (!modalImage || !activeImage) return;

        const source = modalImage.getAttribute('src');
        if (!source) return;

        const request = (this.optimisticMediaRequest || 0) + 1;
        this.optimisticMediaRequest = request;

        const preload = new Image();
        preload.fetchPriority = 'high';
        preload.onload = () => {
          if (this.optimisticMediaRequest !== request) return;

          activeImage.removeAttribute('srcset');
          activeImage.removeAttribute('sizes');
          activeImage.src = source;
          activeImage.alt = modalImage.alt || '';
        };
        preload.src = source;
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const incoming = html.querySelector('variant-selects');
        const current = this.variantSelectors;
        if (!incoming || !current) return;

        const incomingSelects = getProductOptionControls(incoming, 'select');

        getProductOptionControls(current, 'select').forEach((select, index) => {
          const nextSelect = incomingSelects[index];
          if (!nextSelect) return;

          const nextByValue = new Map(
            Array.from(nextSelect.options).map((option) => [option.value, option])
          );

          Array.from(select.options).forEach((option) => {
            const nextOption = nextByValue.get(option.value);
            if (!nextOption) return;

            option.textContent = nextOption.textContent;
            option.disabled = nextOption.disabled;

            [
              'data-product-url',
              'data-option-value-id',
              'data-option-swatch-value',
              'data-option-swatch-focal-point',
            ].forEach((attribute) => {
              const value = nextOption.getAttribute(attribute);
              if (value === null) {
                option.removeAttribute(attribute);
              } else {
                option.setAttribute(attribute, value);
              }
            });
          });

          select.value = nextSelect.value;

          Array.from(select.options).forEach((option) => {
            if (option.value === nextSelect.value) {
              option.setAttribute('selected', 'selected');
            } else {
              option.removeAttribute('selected');
            }
          });
        });

        const currentVariant = current.querySelector('[data-selected-variant]');
        const incomingVariant = incoming.querySelector('[data-selected-variant]');
        if (currentVariant && incomingVariant) {
          currentVariant.textContent = incomingVariant.textContent;
        }
      }

      handleUpdateProductInfo(productUrl, preventPageScroll = false, scrollPhotoOnDesktop = false) {
  return (html) => {
    const variant = this.getSelectedVariant(html);
    const latestVariant = this.getLocalSelectedVariant();
    // A delayed section response must never restore an earlier user selection.
    if (latestVariant && String(latestVariant.id) !== String(variant?.id)) return;

    this.pickupAvailability?.update(variant);
    this.updateOptionValues(html);
    this.updateURL(productUrl, variant?.id);
    this.updateVariantInputs(variant?.id);

    if (!variant) {
      this.setUnavailable();
      return;
    }

    const incomingModelNode = html.querySelector('[data-auzi-variants]');
    if (incomingModelNode && this.dataset.url.split('?')[0].endsWith('/hearloom')) {
      try { this.localVariants = JSON.parse(incomingModelNode.textContent); } catch (_) {}
    }
    this.updateAuziLowStock(variant);
    this.updateHearloomState(variant);

    this.updateMedia(
      html,
      variant?.featured_media?.id,
      preventPageScroll
    );

    const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
      const source = html.getElementById(`${id}-${this.sectionId}`);
      const destination = this.querySelector(`#${id}-${this.dataset.section}`);

      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.toggle('hidden', shouldHide(source));
      }
    };

    updateSourceFromDestination('price');
    updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
    updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
    updateSourceFromDestination('Volume');
    updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

    this.updateQuantityRules(this.sectionId, html);
    this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
    this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

    this.productForm?.toggleSubmitButton(
      html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
      window.variantStrings.soldOut
    );

    publish(PUB_SUB_EVENTS.variantChange, {
      data: {
        sectionId: this.sectionId,
        html,
        variant,
      },
    });

    if (scrollPhotoOnDesktop && window.matchMedia('(min-width: 990px)').matches) {
      window.requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      });
    }
  };
}

      updateVariantInputs(variantId) {
        const variant = this.getLocalSelectedVariant();
        const available = variant?.available === true && String(variant.id) === String(variantId);
        this.querySelectorAll('.auzi-product-payment-terms').forEach(el => el.classList.toggle('is-hidden', !available));
        this.querySelectorAll('shopify-payment-terms').forEach(el => {
          if (variantId) el.setAttribute('variant-id', String(variantId));
          el.hidden = !available;
        });
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        window.dispatchEvent(new CustomEvent('auzi:variant-price-changed', { detail: { variantId, available, priceCents: variant?.price || 0 } }));
      }

      updateURL(url, variantId) {
        const next = new URL(url, window.location.origin);
        const current = new URL(window.location.href);

        // A selected variant must not erase the gifting journey or draft preview.
        // Shopify's native URL update previously replaced every query parameter.
        ['gift', 'gift_family', 'gift_known', 'quiz_device', 'preview_theme_id'].forEach((key) => {
          if (!next.searchParams.has(key) && current.searchParams.has(key)) {
            next.searchParams.set(key, current.searchParams.get(key));
          }
        });
        if (variantId) next.searchParams.set('variant', String(variantId));
        else next.searchParams.delete('variant');

        const nextPath = `${next.pathname}${next.search}${next.hash}`;
        const shareUrl = new URL(nextPath, window.shopUrl);
        shareUrl.searchParams.delete('preview_theme_id');
        this.querySelector('share-button')?.updateUrl(shareUrl.href);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', nextPath);
      }

      setUnavailable() {
        this.querySelectorAll('.auzi-product-payment-terms').forEach(el => el.classList.add('is-hidden'));
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        this.updateAuziLowStock(null);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId, preventPageScroll = false) {
  if (!variantFeaturedMediaId) return;

  const mediaGallerySource = this.querySelector('media-gallery ul');
  const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

  const refreshSourceData = () => {
    if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);

    const mediaGallerySourceItems = Array.from(
      mediaGallerySource.querySelectorAll('li[data-media-id]')
    );

    const sourceSet = new Set(
      mediaGallerySourceItems.map((item) => item.dataset.mediaId)
    );

    const sourceMap = new Map(
      mediaGallerySourceItems.map((item, index) => [
        item.dataset.mediaId,
        { item, index },
      ])
    );

    return [mediaGallerySourceItems, sourceSet, sourceMap];
  };

  if (mediaGallerySource && mediaGalleryDestination) {
    let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

    const mediaGalleryDestinationItems = Array.from(
      mediaGalleryDestination.querySelectorAll('li[data-media-id]')
    );

    const destinationSet = new Set(
      mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId)
    );

    let shouldRefresh = false;

    // add items from new data not present in DOM
    for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
      if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
        mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
        shouldRefresh = true;
      }
    }

    // remove items from DOM not present in new data
    for (let i = 0; i < mediaGallerySourceItems.length; i++) {
      if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
        mediaGallerySourceItems[i].remove();
        shouldRefresh = true;
      }
    }

    // refresh
    if (shouldRefresh) {
      [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
    }

    // if media galleries don't match, sort to match new data order
    mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
      const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

      if (sourceData && sourceData.index !== destinationIndex) {
        mediaGallerySource.insertBefore(
          sourceData.item,
          mediaGallerySource.querySelector(
            `li:nth-of-type(${destinationIndex + 1})`
          )
        );

        // refresh source now that it has been modified
        [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
      }
    });
  }

  // set featured media as active in the media gallery
  this.querySelector(`media-gallery`)?.setActiveMedia?.(
    `${this.dataset.section}-${variantFeaturedMediaId}`,
    true,
    preventPageScroll
  );

  // update media modal
  const modalContent = this.productModal?.querySelector(
    `.product-media-modal__content`
  );

  const newModalContent = html.querySelector(
    `product-modal .product-media-modal__content`
  );

  if (modalContent && newModalContent) {
    modalContent.innerHTML = newModalContent.innerHTML;
  }
}

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
